import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    IconButton,
    TextField,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { Delete, Add } from '@mui/icons-material';
import { getStoresByInstitutionId } from '../../services/store.service';
import NotInChargeNotice from '../store/NotInChargeNotice';
import { Store } from '../../interfaces/store.interface';
import { ItemService } from '../../services/item.service';
import { Item } from '../../interfaces/item.interface';
import { StockService } from '../../services/stock.service';
import { StockTransferService } from '../../services/stockTransfer.service';
import { StockTransferLineCreateDTO } from '../../interfaces/stockTransfer.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import SearchableSelect from '../common/SearchableSelect';
import { helpWhy } from '../../help/why';
import SerialNumbersField from '../serial/SerialNumbersField'; // F6b
import ItemScanField, { ScanOutcome } from '../item/ItemScanField';   // Q3

interface LineInput {
    itemId: string;
    quantity: string;
    serials?: string[];   // F6b
}

const StockTransferCreate: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const [stores, setStores] = useState<Store[]>([]);
    const [storesLoaded, setStoresLoaded] = useState(false);
    const [items, setItems] = useState<Item[]>([]);
    const [fromStoreId, setFromStoreId] = useState<string>('');
    const [toStoreId, setToStoreId] = useState<string>('');
    const [transferDate, setTransferDate] = useState<string>('');
    const [notes, setNotes] = useState<string>('');
    const [lines, setLines] = useState<LineInput[]>([{ itemId: '', quantity: '' }]);
    const [submitting, setSubmitting] = useState<boolean>(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [storeResponse, itemResponse] = await Promise.all([
                    getStoresByInstitutionId(0, 100),
                    ItemService.getItemsByInstitution(0, 200, '', 'STOCK'), // FW-2A
                ]);
                setStores(storeResponse.content);
                setStoresLoaded(true);
                setItems(itemResponse.content);
            } catch (err) {
                console.error('Error loading stores/items:', err);
                toast.showApiError(err, 'Failed to load stores or items');
            }
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFromStoreChange = (id: string) => {
        setFromStoreId(id);
        setLines((prev) => prev.map((l) => ({ ...l, serials: [] })));   // F6b: picked serials belong to the old store
    };

    const handleToStoreChange = (id: string) => {
        setToStoreId(id);
    };

    const handleLineChange = (index: number, field: 'itemId' | 'quantity', value: string) => {
        setLines((prev) =>
            prev.map((line, i) => (i === index
                ? { ...line, [field]: value, ...(field === 'itemId' ? { serials: [] } : {}) }
                : line)),
        );
    };

    // ---- F6b: serial-tracked lines pick their serials from the source store ----
    const itemById = new Map(items.map((it) => [it.id, it]));
    const isSerialLine = (line: LineInput) => !!itemById.get(line.itemId)?.serialTracked;
    const handleSerialsChange = (index: number, serials: string[]) => {
        setLines((prev) => prev.map((line, i) => (i === index ? { ...line, serials } : line)));
    };

    // ---- Q3: scan-to-add. A scanned item adds 1 to its line, fills the empty first line, or starts a new line. ----
    const linesRef = React.useRef<LineInput[]>([]);
    linesRef.current = lines;
    const handleScan = async (item: Item): Promise<ScanOutcome> => {
        const label = `${item.itemCode} - ${item.itemName}`;
        if ((item.itemKind ?? 'STOCK') !== 'STOCK') {
            return { ok: false, message: `${label} is not a stock item, so it cannot be transferred.` };
        }
        // When the source store is chosen, never scan past what it holds. A failed stock read leaves the quantity uncapped.
        let available: number | null = null;
        if (fromStoreId && item.id) {
            try {
                const stock = await StockService.getStockByStoreAndItem(fromStoreId, item.id);
                available = stock?.availableQty ?? 0;
            } catch {
                available = null;
            }
        }
        const current = linesRef.current;
        const existing = current.findIndex((l) => l.itemId === item.id);
        let qty = 1;
        let next: LineInput[];
        const wanted = existing >= 0 ? (Number(current[existing].quantity) || 0) + 1 : 1;
        if (available !== null && wanted > available) {
            return { ok: false, message: `${label}: only ${available} in the source store, so the quantity stays${existing >= 0 ? ` at ${Number(current[existing].quantity) || 0}` : ' unchanged'}.` };
        }
        if (existing >= 0) {
            qty = wanted;
            next = current.map((l, i) => (i === existing ? { ...l, quantity: String(qty) } : l));
        } else {
            const blank = current.findIndex((l) => !l.itemId && l.quantity.trim() === '');
            const line: LineInput = { itemId: item.id!, quantity: '1', serials: [] };
            next = blank >= 0 ? current.map((l, i) => (i === blank ? line : l)) : [...current, line];
        }
        linesRef.current = next;
        setLines(next);
        setItems((prev) => (prev.some((it) => it.id === item.id) ? prev : [...prev, item]));
        const serialNote = item.serialTracked ? ' Pick its serial numbers on the line.' : '';
        return { ok: true, message: `${label}: quantity is now ${qty}.${serialNote}` };
    };

    const handleAddLine = () => {
        setLines((prev) => [...prev, { itemId: '', quantity: '' }]);
    };

    const handleRemoveLine = (index: number) => {
        setLines((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) {
            toast.showError('No institution selected');
            return;
        }
        if (!fromStoreId) {
            toast.showError('Please select a source store');
            return;
        }
        if (!toStoreId) {
            toast.showError('Please select a destination store');
            return;
        }
        if (fromStoreId === toStoreId) {
            toast.showError('Source and destination stores must be different');
            return;
        }

        const payloadLines: StockTransferLineCreateDTO[] = [];
        for (const line of lines) {
            if (!line.itemId && line.quantity.trim() === '') continue;
            if (!line.itemId) {
                toast.showError('Please select an item for every line');
                return;
            }
            const qty = Number(line.quantity);
            if (line.quantity.trim() === '' || Number.isNaN(qty) || qty <= 0) {
                toast.showError('Enter a valid positive quantity for every line');
                return;
            }
            if (isSerialLine(line)) {   // F6b
                if (!Number.isInteger(qty) || (line.serials?.length ?? 0) !== qty) {
                    toast.showError('Pick one serial number per unit for every serial-tracked line');
                    return;
                }
                payloadLines.push({ itemId: line.itemId, quantity: qty, serials: line.serials });
                continue;
            }
            payloadLines.push({ itemId: line.itemId, quantity: qty });
        }
        if (payloadLines.length === 0) {
            toast.showError('Add at least one transfer line');
            return;
        }

        try {
            setSubmitting(true);
            const created = await StockTransferService.createStockTransfer({
                fromStoreId,
                toStoreId,
                institutionId,
                transferDate: transferDate || undefined,
                notes: notes.trim() || undefined,
                lines: payloadLines,
            });
            toast.showSuccess('Stock transfer created');
            navigate(`/stock-transfers/${created.id}`);
        } catch (err) {
            console.error('Error creating stock transfer:', err);
            toast.showApiError(err, 'Failed to create stock transfer');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ p: 3 }} data-help-page="store.transferForm.page">
            <Typography variant="h4" sx={{ mb: 3 }}>
                New Stock Transfer
            </Typography>
            {storesLoaded && stores.length === 0 && <NotInChargeNotice />}
            <Paper elevation={3} sx={{ p: 3, mb: 3, maxWidth: 720 }} data-help-section="store.transferForm.header">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
                    <SearchableSelect<Store>
                        options={stores}
                        value={fromStoreId}
                        onChange={handleFromStoreChange}
                        getOptionLabel={store => store.storeName}
                        data-help="store.transferForm.field.from" label="From Store"
                        sx={{ flex: '1 1 240px' }}
                    />
                    <SearchableSelect<Store>
                        options={stores}
                        value={toStoreId}
                        onChange={handleToStoreChange}
                        getOptionLabel={store => store.storeName}
                        data-help="store.transferForm.field.to" label="To Store"
                        sx={{ flex: '1 1 240px' }}
                    />
                </Box>

                <TextField
                    data-help="store.transferForm.field.date" label="Transfer Date"
                    type="date"
                    fullWidth
                    value={transferDate}
                    onChange={(e) => setTransferDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ mb: 3 }}
                />

                <TextField
                    data-help="store.transferForm.field.notes" label="Notes"
                    fullWidth
                    multiline
                    minRows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    sx={{ mb: 1 }}
                />
            </Paper>

            <Paper elevation={3} sx={{ p: 3, mb: 3 }} data-help-section="store.transferForm.lines">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="h6">Transfer Lines</Typography>
                    <Button data-help="store.transferForm.addLine" startIcon={<Add />} onClick={handleAddLine}>
                        Add Line
                    </Button>
                </Box>
                <Box data-help="store.transferForm.scan">
                <ItemScanField data-testid="transfer-scan" onItem={handleScan}
                    label="Scan or type item barcode to add a line" />
                </Box>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell data-help="store.transferForm.col.no">#</TableCell>
                                <TableCell data-help="store.transferForm.col.item">Item</TableCell>
                                <TableCell data-help="store.transferForm.col.qty" align="right">Quantity</TableCell>
                                <TableCell data-help="store.transferForm.col.remove" align="center">Remove</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {lines.map((line, index) => (
                                <TableRow key={index}>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>
                                        <SearchableSelect<Item>
                                            options={items}
                                            value={line.itemId}
                                            onChange={id => handleLineChange(index, 'itemId', id)}
                                            getOptionLabel={item => `${item.itemCode} - ${item.itemName}`}
                                            data-help="store.transferForm.field.item" placeholder="Select item"
                                            sx={{ minWidth: 240 }}
                                        />
                                        {isSerialLine(line) && (   /* F6b */
                                            <Box sx={{ mt: 1, minWidth: 240 }}>
                                                <SerialNumbersField mode="pick" expected={Number(line.quantity) || 0}
                                                    value={line.serials ?? []} onChange={(v) => handleSerialsChange(index, v)}
                                                    itemId={line.itemId} storeId={fromStoreId || null}
                                                    disabled={!fromStoreId} data-testid={`transfer-serials-${index}`} />
                                            </Box>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <TextField
                                            size="small"
                                            type="number"
                                            data-help="store.transferForm.field.qty" value={line.quantity}
                                            onChange={(e) =>
                                                handleLineChange(index, 'quantity', e.target.value)
                                            }
                                            sx={{ width: 120 }}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            data-help="store.transferForm.action.remove"
                                            size="small"
                                            color="error"
                                            onClick={() => handleRemoveLine(index)}
                                            disabled={lines.length === 1}
                                            aria-label="Remove line"
                                        >
                                            <Delete />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                    data-help="store.transferForm.create"
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting || !fromStoreId || !toStoreId}
                    {...helpWhy(!submitting && (!fromStoreId || !toStoreId) ? { kind: 'status', message: 'Choose both the From store and the To store first.' } : null)}
                >
                    {submitting ? 'Creating...' : 'Create Transfer'}
                </Button>
                <Button data-help="store.transferForm.cancel" variant="outlined" onClick={() => navigate('/stock-transfers')}>
                    Cancel
                </Button>
            </Box>
        </Box>
    );
};

export default StockTransferCreate;
